import produce from "immer";
import undoable from "redux-undo";
import { createAction, createReducer } from "redux-act";
import { ClusterSymbolDc, StyleDc } from "@evergis/api";
import { RangeValue } from "@evergis/ui";

import { reducerRegistry } from "../reducerRegistry";
import { extractStyle } from "../../evergis/core/symbol/compositeSymbol";
import { DEFAULT_SCALE_RANGE } from "../../modules/Style/constants";
import { LabelType, StyleSymbolType, RasterSymbol, isClusterSymbol } from "../../interfaces/style";

export const reducerName = "style";

export type Style = Omit<StyleDc, "symbol" | "children"> & {
  symbol: StyleSymbolType;
  label: LabelType | null;
  raster: RasterSymbol | null;
  ignoreLabel?: boolean;
  minResolution?: number;
  maxResolution?: number;
  children: Style[];
};

export type StyleState = {
  style?: Style;
  scaleSegments?: number[];
  selectedSegment?: number;
  valueIndex?: number;
  hiddenLegends?: Record<string, string[]>;
  defaultScaleRange?: RangeValue;
};

const initialState: StyleState = {
  defaultScaleRange: DEFAULT_SCALE_RANGE,
  hiddenLegends: {},
};

export const setStyle = createAction<StyleDc>("style/set");
export const setScaleSegments = createAction<number[]>("style/set-scale-segments");
export const setDefaultScaleRange = createAction<RangeValue>("style/set-default-scale-range");
export const setSelectedSegment = createAction<number>("style/set-selected-segment");
export const setValueIndex = createAction<number>("style/set-value-index");
export const updateChild = createAction<{ style: Style; segmentIndex?: number; valueIndex?: number }>(
  "style/update-child",
);
export const updateSymbol = createAction<{
  symbol: StyleSymbolType;
  segmentIndex?: number;
  valueIndex?: number;
  isClusterSingleObject?: boolean;
}>("style/update-symbol");
export const updateLabel = createAction<{ label: LabelType; index?: number }>("style/update-label");
export const createChildrenSymbol = createAction<Style>("style/create-children-symbol");
export const updateChildrenSymbol = createAction<{
  symbol: StyleSymbolType;
  selectedSegment?: number;
  index?: number;
}>("style/update-children-symbol");
export const partialUpdateSymbol = createAction<{
  symbol: Partial<StyleSymbolType>;
  index?: number;
  isClusterSingleObject?: boolean;
}>("style/partial-update-symbol");
export const resetStyle = createAction<RangeValue | void>("style/reset");
export const removeSymbolClassification = createAction("style/remove-symbol-classification");
export const removeClassificationByResolution = createAction<number>("style/remove-classification-by-resolution");

export const undo = createAction("style/undo");
export const redo = createAction("style/redo");
export const resetHistory = createAction("style/reset-history");
export const setIgnoreLabel = createAction<{ isIgnore: boolean; index?: number }>("style/set-ignore-label");
export const toggleHiddenLegend = createAction<{ layerName: string; hiddenCondition: string }>(
  "style/toggle-hidden-legend",
);
export const clearHiddenLegends = createAction<string>("style/clear-hidden-legends");

const baseReducer = createReducer({}, initialState);

baseReducer.on(
  setStyle,
  produce((state: StyleState, style) => {
    state.style = extractStyle(style);
  }),
);

baseReducer.on(
  setScaleSegments,
  produce((state: StyleState, scaleSegments) => {
    state.scaleSegments = scaleSegments;
  }),
);

baseReducer.on(
  setDefaultScaleRange,
  produce((state: StyleState, defaultScaleRange) => {
    state.defaultScaleRange = defaultScaleRange;
  }),
);

baseReducer.on(
  setSelectedSegment,
  produce((state: StyleState, selectedSegment) => {
    state.selectedSegment = selectedSegment;
  }),
);

baseReducer.on(
  setValueIndex,
  produce((state: StyleState, valueIndex) => {
    state.valueIndex = valueIndex;
  }),
);

baseReducer.on(
  updateChild,
  produce((state: StyleState, { style, segmentIndex, valueIndex }) => {
    if (segmentIndex >= 0) {
      if (valueIndex >= 0) {
        state.style.children[segmentIndex].children[valueIndex] = style;
      } else {
        state.style.children[segmentIndex] = style;
      }
    } else if (valueIndex >= 0) {
      state.style.children[valueIndex] = style;
    } else {
      state.style = style;
    }
  }),
);

baseReducer.on(
  updateSymbol,
  produce((state: StyleState, { symbol, segmentIndex, valueIndex, isClusterSingleObject }) => {
    if (segmentIndex >= 0) {
      if (valueIndex >= 0) {
        if (isClusterSingleObject) {
          (state.style.children[segmentIndex].children[valueIndex].symbol as ClusterSymbolDc).singleObjectSymbol =
            symbol;
        } else {
          state.style.children[segmentIndex].children[valueIndex].symbol = symbol;
        }
      } else if (isClusterSingleObject) {
        (state.style.children[segmentIndex].symbol as ClusterSymbolDc).singleObjectSymbol = symbol;
      } else {
        state.style.children[segmentIndex].symbol = symbol;
      }
    } else if (valueIndex >= 0) {
      if (isClusterSingleObject) {
        (state.style.children[valueIndex].symbol as ClusterSymbolDc).singleObjectSymbol = symbol;
      } else {
        state.style.children[valueIndex].symbol = symbol;
      }
    } else if (isClusterSingleObject) {
      (state.style.symbol as ClusterSymbolDc).singleObjectSymbol = symbol;
    } else {
      state.style.symbol = symbol;
    }
  }),
);

baseReducer.on(
  partialUpdateSymbol,
  produce((state: StyleState, { symbol, index, isClusterSingleObject }) => {
    if (index >= 0) {
      if (isClusterSingleObject) {
        (state.style.children[index].symbol as ClusterSymbolDc).singleObjectSymbol = {
          ...(state.style.children[index].symbol as ClusterSymbolDc).singleObjectSymbol,
          ...symbol,
        };
      } else {
        state.style.children[index].symbol = { ...state.style.children[index].symbol, ...symbol };
      }
    } else if (isClusterSingleObject) {
      (state.style.symbol as ClusterSymbolDc).singleObjectSymbol = {
        ...(state.style.symbol as ClusterSymbolDc).singleObjectSymbol,
        ...symbol,
      };
    } else {
      state.style.symbol = { ...state.style.symbol, ...symbol };
    }
  }),
);

baseReducer.on(
  updateLabel,
  produce((state: StyleState, { label, index }) => {
    const isCluster = isClusterSymbol(state.style.symbol);

    if (index >= 0) {
      if (isCluster) {
        (state.style.children[index].symbol as ClusterSymbolDc).labelSymbol = label;
      } else {
        state.style.children[index].label = label;
      }
    } else if (isCluster) {
      (state.style.symbol as ClusterSymbolDc).labelSymbol = label;
    } else {
      state.style.label = label;
    }
  }),
);

baseReducer.on(
  setIgnoreLabel,
  produce((state: StyleState, { isIgnore, index }) => {
    if (index >= 0) {
      state.style.children[index].ignoreLabel = isIgnore;
    } else {
      state.style.ignoreLabel = isIgnore;
    }
  }),
);

baseReducer.on(
  removeSymbolClassification,
  produce((state: StyleState) => {
    state.style.children = null;
  }),
);

baseReducer.on(
  removeClassificationByResolution,
  produce((state: StyleState, minResolution: number) => {
    state.style.children = state.style.children?.filter(child => child.minResolution !== minResolution) || null;
  }),
);

baseReducer.on(
  createChildrenSymbol,
  produce((state: StyleState, symbol: Style) => {
    state.style.children = state.style.children?.length ? [...state.style.children, symbol] : [symbol];
  }),
);

baseReducer.on(
  updateChildrenSymbol,
  produce((state: StyleState, { symbol, index }) => {
    state.style.children[index].symbol = symbol;
  }),
);

baseReducer.on(
  toggleHiddenLegend,
  produce((state: StyleState, { layerName, hiddenCondition }) => {
    if (!state.hiddenLegends[layerName]) {
      state.hiddenLegends[layerName] = [];
    }

    const index = state.hiddenLegends[layerName].indexOf(hiddenCondition);

    if (index >= 0) {
      state.hiddenLegends[layerName].splice(index, 1);
    } else {
      state.hiddenLegends[layerName].push(hiddenCondition);
    }
  }),
);

baseReducer.on(
  clearHiddenLegends,
  produce((state: StyleState, layerName) => {
    state.hiddenLegends[layerName] = [];
  }),
);

baseReducer.on(resetStyle, ({ defaultScaleRange }: StyleState) => ({
  ...initialState,
  scaleSegments: defaultScaleRange,
}));

const reducer = undoable(baseReducer, {
  undoType: undo.getType(),
  redoType: redo.getType(),
  clearHistoryType: resetHistory.getType(),
  ignoreInitialState: true,
});

reducerRegistry.register(reducerName, reducer);
